import { useCallback, useState } from 'react';
import { Text, Box, Flex, Icon, IconButton } from '@chakra-ui/react';
import {
  ArrowLeft,
  ArrowRight,
  ArrowsOutSimple,
  MapPin,
  YoutubeLogo,
} from '@phosphor-icons/react';
import { Swiper, SwiperSlide } from 'swiper/react';
import SwiperCore, { Navigation, Thumbs } from 'swiper';
import getVideoId from 'get-video-id';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import Image from '@/lib/Image';
import ListingImagesModalButton from '../ListingImagesModal';

// eslint-disable-next-line import/no-unresolved
import 'swiper/css';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/navigation';
// eslint-disable-next-line import/no-unresolved
import 'swiper/css/thumbs';
import { useBoundingRectOnce } from '@/lib/hooks/useBoundingRect';

const ReactMap = dynamic(() => import('@/components/common/Maps/ReactMap'), {
  ssr: false,
});

interface CustomSwiperProps {
  images: {
    src: string;
    blurdata_url?: string | undefined | null;
    caption?: string | null | undefined;
  }[];
  location?: GeoJSON.GeoJSON | null;
  video?: string | null;
  title: string; // for alt texts
}

const height = '660px';

const thumbnailSize = ['32px', '48px', '64px'];

const CustomSwiper = ({
  images,
  location,
  video,
  title,
}: CustomSwiperProps) => {
  const mapIndex = location ? 0 : false;
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperCore>();
  const videoObj = video ? getVideoId(video) : null;
  const [mapWrapperRef, dimensions] = useBoundingRectOnce();
  const { t } = useTranslation('frontoffice');

  const setSwipeAllowed = useCallback(
    (swiper: SwiperCore) => {
      // NOTE: a hack basically. For whatever reason Swiper doesn't
      // let using 'allowTouchMove' -prop dynamically.
      if (mapIndex !== false) {
        if (swiper.activeIndex === mapIndex) {
          // eslint-disable-next-line no-param-reassign
          swiper.allowTouchMove = false;
        } else if (!swiper.allowTouchMove) {
          // eslint-disable-next-line no-param-reassign
          swiper.allowTouchMove = true;
        }
      }
    },
    [mapIndex],
  );

  return (
    <Box
      position="relative"
      sx={{
        '.custom-swiper': {
          height: ['auto', null, null, height],
        },
        '.thumb-swiper .swiper-slide': {
          width: thumbnailSize,
          opacity: 0.75,
          borderRadius: '4px',
          overflow: 'hidden',
        },
        '.thumb-swiper svg': {
          maxWidth: '100%',
        },
      }}
    >
      <IconButton
        className="prev-arrow"
        _disabled={{ opacity: 0 }}
        isRound
        size="lg"
        _hover={{ backgroundColor: 'primary.200' }}
        _active={{ backgroundColor: 'primary.400' }}
        backgroundColor="primary.50"
        aria-label="prev slide"
        cursor="pointer"
        pos="absolute"
        transform="translateY(-50%)"
        borderRadius="full"
        left="5"
        top="50%"
        zIndex={200}
        icon={<ArrowLeft weight="bold" />}
      />
      <IconButton
        className="next-arrow"
        _disabled={{ opacity: 0 }}
        isRound
        size="lg"
        _hover={{ backgroundColor: 'primary.200' }}
        _active={{ backgroundColor: 'primary.400' }}
        backgroundColor="primary.50"
        aria-label="next slide"
        cursor="pointer"
        pos="absolute"
        transform="translateY(-50%)"
        borderRadius="full"
        right="5"
        top="50%"
        zIndex={200}
        icon={<ArrowRight weight="bold" />}
      />
      {/* the big carousel */}
      <Swiper
        modules={[Navigation, Thumbs]}
        spaceBetween={0}
        navigation={{ nextEl: '.next-arrow', prevEl: '.prev-arrow' }}
        thumbs={{ swiper: thumbsSwiper }}
        className="custom-swiper"
        initialSlide={location && images.length > 0 ? 1 : 0}
        onActiveIndexChange={setSwipeAllowed}
        onAfterInit={setSwipeAllowed}
      >
        {location && (
          <SwiperSlide key="map">
            {({ isActive }) =>
              isActive ? (
                <Box
                  borderRadius="base"
                  overflow="hidden"
                  width="100%"
                  height={['480px', null, height]}
                  ref={mapWrapperRef}
                >
                  <ReactMap
                    geoJson={location}
                    dimensions={dimensions}
                    showUserLocation
                    cooperativeGestures
                  />
                </Box>
              ) : null
            }
          </SwiperSlide>
        )}
        {images.length > 0
          ? images.map((image, index) => (
              <SwiperSlide key={image.src}>
                <Box
                  width="100%"
                  position="relative"
                  lineHeight="1"
                  sx={{
                    '> span:first-of-type': {
                      pos: [null, null, null, 'absolute !important'],
                      height: [null, null, null, `${height} !important`],
                      verticalAlign: 'middle',
                    },
                  }}
                >
                  <Image
                    width="1080"
                    height={height}
                    src={image.src}
                    placeholder={image.blurdata_url ? 'blur' : 'empty'}
                    blurDataURL={image.blurdata_url || undefined}
                    priority={!index} // first loads hard
                    alt={`${title} ${index + 1}`}
                  />
                  {
                    // don't show caption for first image
                    // as per designer requirements
                    index > 0 && image.caption ? (
                      <Flex
                        position="absolute"
                        width="100%"
                        alignItems="center"
                        justifyContent="center"
                        top="10px"
                      >
                        <Box
                          borderRadius="4px"
                          backgroundColor="rgba(0,0,0,0.65)"
                          padding="4px 8px"
                          color="white"
                          fontSize="12px"
                          lineHeight="18px"
                        >
                          {image.caption}
                        </Box>
                      </Flex>
                    ) : null
                  }
                </Box>
              </SwiperSlide>
            ))
          : null}
        {videoObj && videoObj.service === 'youtube' && videoObj.id && (
          <SwiperSlide key="youtube">
            {({ isActive }) =>
              isActive ? (
                <Flex
                  height={['480px', null, height]}
                  width="100%"
                  justifyContent="center"
                >
                  <iframe
                    title="Video"
                    height="100%"
                    width="100%"
                    src={`https://www.youtube.com/embed/${
                      videoObj.id as string
                    }`}
                    allowFullScreen
                  />
                </Flex>
              ) : null
            }
          </SwiperSlide>
        )}
        {!videoObj && !location && !images.length ? (
          <SwiperSlide>
            <Flex
              width="100%"
              height={height}
              alignItems="center"
              justifyContent="center"
              position="relative"
            >
              <Text display="flex">{t('no_images', 'Ei kuvia kohteesta')}</Text>
            </Flex>
          </SwiperSlide>
        ) : null}
      </Swiper>
      {/* the thumbnail carousel */}
      <Box
        width={['320px', '320px', '640px']}
        backgroundColor="white"
        backdropFilter="blur(5px)"
        pt={[2, '10px']}
        borderTopLeftRadius={['md', 'lg']}
        borderTopRightRadius={['md', 'lg']}
        position="absolute"
        bottom="0"
        left="50%"
        transform="translateX(-50%)"
        zIndex="200"
        sx={{
          '.swiper': {
            px: [2, '10px'],
          },
        }}
      >
        <IconButton
          className="prev-arrow-sm"
          _disabled={{ opacity: 0 }}
          isRound
          size="sm"
          _hover={{ backgroundColor: 'primary.200' }}
          _active={{ backgroundColor: 'primary.300', color: 'white' }}
          backgroundColor="primary.50"
          aria-label="prev slide"
          cursor="pointer"
          pos="absolute"
          transform="translateY(-36%)"
          borderRadius="full"
          left="-6"
          top="50%"
          zIndex={200}
          icon={<ArrowLeft weight="bold" />}
        />
        <IconButton
          className="next-arrow-sm"
          _disabled={{ opacity: 0 }}
          isRound
          size="sm"
          _hover={{ backgroundColor: 'primary.200' }}
          _active={{ backgroundColor: 'primary.300', color: 'white' }}
          backgroundColor="primary.50"
          aria-label="next slide"
          cursor="pointer"
          pos="absolute"
          transform="translateY(-36%)"
          borderRadius="full"
          right="-6"
          top="50%"
          zIndex={200}
          icon={<ArrowRight weight="bold" />}
        />
        <Swiper
          modules={[Navigation]}
          navigation={{ nextEl: '.next-arrow-sm', prevEl: '.prev-arrow-sm' }}
          onSwiper={setThumbsSwiper}
          spaceBetween={10}
          slidesPerView="auto"
          freeMode
          watchSlidesProgress
          className="thumb-swiper"
        >
          {location && (
            <SwiperSlide key="map">
              <Flex
                overflow="hidden"
                background="gray.200"
                alignItems="center"
                justifyContent="center"
                width={thumbnailSize}
                height={thumbnailSize}
                pb={0}
                sx={{ cursor: 'pointer', position: 'relative' }}
              >
                <Icon width={8} height={8} as={MapPin} />
              </Flex>
            </SwiperSlide>
          )}
          {images.length > 0
            ? images.map((image, index) => (
                <SwiperSlide key={image.src}>
                  <Box
                    overflow="hidden"
                    width={thumbnailSize}
                    height={thumbnailSize}
                    pb={2}
                    sx={{ cursor: 'pointer', position: 'relative' }}
                  >
                    <Image
                      width={thumbnailSize[thumbnailSize.length - 1]}
                      height={thumbnailSize[thumbnailSize.length - 1]}
                      src={image.src}
                      placeholder={image.blurdata_url ? 'blur' : 'empty'}
                      blurDataURL={image.blurdata_url || undefined}
                      alt={`${title} ${index + 1}`}
                      priority={!index}
                    />
                  </Box>
                </SwiperSlide>
              ))
            : null}
          {videoObj && videoObj.service && (
            <SwiperSlide key="youtube">
              <Flex
                overflow="hidden"
                background="gray.200"
                alignItems="center"
                justifyContent="center"
                width={thumbnailSize}
                height={thumbnailSize}
                pb={0}
                sx={{ cursor: 'pointer', position: 'relative' }}
              >
                <Icon width={8} height={8} as={YoutubeLogo} />
              </Flex>
            </SwiperSlide>
          )}
        </Swiper>
      </Box>
      {images.length > 0 || video ? (
        <ListingImagesModalButton images={images} video={video}>
          {({ onOpen }) => (
            <IconButton
              onClick={() => onOpen()}
              _disabled={{ opacity: 0 }}
              isRound
              size="sm"
              _hover={{ backgroundColor: 'primary.200' }}
              _active={{ backgroundColor: 'primary.300', color: 'white' }}
              backgroundColor="primary.50"
              aria-label={t(
                'open_images_modal',
                'Avaa kaikki kuvat uuteen ikkunaan',
              )}
              cursor="pointer"
              pos="absolute"
              borderRadius="full"
              right="29px"
              top="18px"
              zIndex={200}
              icon={<ArrowsOutSimple />}
            />
          )}
        </ListingImagesModalButton>
      ) : null}
    </Box>
  );
};

export default CustomSwiper;
